/**
 * SocialFields — Instagram / TikTok / YouTube / Website inputs.
 *
 * Persists into `user_business_context.social_channels` (jsonb). Shape:
 * `{ instagram, tiktok, youtube, website }` — each optional string.
 */

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { InstagramLogo, TiktokLogo, YoutubeLogo, Globe } from "@phosphor-icons/react";

export interface SocialChannels {
  instagram?: string;
  tiktok?: string;
  youtube?: string;
  website?: string;
}

export interface SocialFieldsProps {
  value: SocialChannels;
  onChange: (next: SocialChannels) => void;
}

const FIELDS: Array<{
  key: keyof SocialChannels;
  label: string;
  placeholder: string;
  icon: typeof InstagramLogo;
  prefix?: string;
}> = [
  { key: "instagram", label: "Instagram", placeholder: "seu_handle", icon: InstagramLogo, prefix: "@" },
  { key: "tiktok", label: "TikTok", placeholder: "seu_handle", icon: TiktokLogo, prefix: "@" },
  { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@canal", icon: YoutubeLogo },
  { key: "website", label: "Site", placeholder: "https://seusite.com", icon: Globe },
];

function normalizeHandle(raw: string): string {
  return raw.trim().replace(/^@+/, "");
}

export function SocialFields({ value, onChange }: SocialFieldsProps) {
  const handle = (key: keyof SocialChannels, raw: string) => {
    const normalized =
      key === "instagram" || key === "tiktok" ? normalizeHandle(raw) : raw.trim();
    onChange({ ...value, [key]: normalized || undefined });
  };

  return (
    <div className="space-y-4">
      {FIELDS.map((f) => {
        const Icon = f.icon;
        return (
          <div key={f.key} className="space-y-2">
            <Label htmlFor={`social-${f.key}`} className="flex items-center gap-2">
              <Icon weight="duotone" className="w-4 h-4 text-primary" />
              {f.label}
            </Label>
            <div className="relative">
              {f.prefix ? (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  {f.prefix}
                </span>
              ) : null}
              <Input
                id={`social-${f.key}`}
                value={value[f.key] ?? ""}
                placeholder={f.placeholder}
                className={f.prefix ? "pl-7" : undefined}
                onChange={(e) => handle(f.key, e.target.value)}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
